import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import "../../Assets/Styles/pages/homeRedesign.scss";
import heroImage from "../../Assets/Images/landing/home-hero.png";

import { ClearCache, setBatchImages, setID } from "../../Redux/UserSlice/userSlice";
import { deleteAll } from "../../Redux/WaterMarks/watermarks";
import { ClearImage } from "../../Redux/MainImage/imageSlice";
import AppSidebar from "../../Components/Layout/AppSidebar";
import AppTopbar from "../../Components/Layout/AppTopbar";
import SettingsModal from "../../Components/SettingsModal/settingsModal";
import SignIn from "../../Components/SignIn/signIn";
import FeatureCard from "./FeatureCard";
import WatermarkSingleImageUpload from "./WatermarkSingleImageUpload";
import WatermarkTemplateCreate from "./WatermarkTemplateCreate";
import type { RootState } from "../../Redux/store";
import usePageMetadata from "../../Utils/usePageMetadata";

const Home: React.FC = () => {
  usePageMetadata("eZy WaterMark | Add Watermarks to Your Photos");
  const dispatch = useDispatch();
  const userSliceState = useSelector((state: RootState) => state.userSlice);
  const isLoggedIn = Boolean(Object.keys(userSliceState.user_Auth).length);

  const history = useHistory();
  const [openSettings, setOpenSettings] = useState(false);
  const [openSignIn, setOpenSignIn] = useState(false);

  useEffect(() => {
    const handlePopstate = (event: PopStateEvent) => {
      event.preventDefault();
      dispatch(ClearCache());
    };

    window.addEventListener("popstate", handlePopstate);

    return () => {
      window.removeEventListener("popstate", handlePopstate);
    };
  }, [dispatch]);

  useEffect(() => {
    const handlePopState = () => {
      history.push("/home");
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [history]);

  useEffect(() => {
    if (!isLoggedIn) history.push("/home");
  }, [isLoggedIn, history]);

  useEffect(() => {
    dispatch(setBatchImages(false));
    dispatch(deleteAll());
    dispatch(ClearImage());
    // Clear any template selected via the batch/"watermark multiple images" flow
    // (SelectTemplate -> setID). ClearImage empties `batchurls`, but the selected template
    // ID lives in userSlice and would otherwise linger and get silently auto-applied the next
    // time the user uploads a single image (ImageSourceSelectorModal reads current_TemplateID).
    // Same reset resetEditor() already does when leaving the editor.
    dispatch(setID(null));
  }, [dispatch]);

  return (
    <div className="home-shell home-page-shell">
      <AppSidebar
        active="home"
        isLoggedIn={isLoggedIn}
        setOpenSignIn={setOpenSignIn}
        showPremiumUpsell
      />

      <div className="d-flex flex-column flex-fill">
        <AppTopbar
          isLoggedIn={isLoggedIn}
          setOpenSignIn={setOpenSignIn}
          setOpenSettings={setOpenSettings}
        />

        <div className="home-main">
          <section className="home-hero">
            <div className="home-hero__copy">
              <div className="home-hero__eyebrow">
                <span className="home-hero__eyebrow-icon" aria-hidden="true">✦</span>
                Professional watermarking made simple
              </div>
              <h1 className="home-hero__title">
                Create stunning
                <br />
                <span>watermarks</span> for
                <br />
                your photos
              </h1>
              <p className="home-hero__subtitle">
                Upload an image and add text, signature, logo or QR code as a
                watermark in just a few clicks.
              </p>
              <div className="home-hero__trust">
                <span className="home-hero__trust-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3 4 6.5V11c0 4.6 3.2 8.9 8 10 4.8-1.1 8-5.4 8-10V6.5L12 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                    <path d="m9 12 2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Your images stay on your device
              </div>
            </div>
            <div className="home-hero__image-wrap">
              <img src={heroImage} alt="" className="home-hero__image" />
            </div>
          </section>

          <WatermarkSingleImageUpload />

          <div className="home-feature-cards">
            <FeatureCard
              color="green"
              onClick={() =>
                isLoggedIn ? history.push("/batch-watermark") : setOpenSignIn(true)
              }
              title="Batch Watermark"
              description="Apply watermark to hundreds of images at once."
              icon={
                // imageIcon.svg is a solid white silhouette (no stroke), so it's recolored
                // green via CSS mask rather than rendered directly -- see .image-icon-mask.
                <span className="image-icon-mask image-icon-mask--green" />
              }
            />
            <WatermarkTemplateCreate isLoggedIn={isLoggedIn} setOpenSignIn={setOpenSignIn} />
          </div>
        </div>
      </div>

      <SettingsModal
        open={openSettings}
        setOpen={setOpenSettings}
        handleDone={() => setOpenSettings(false)}
        parent={false}
      />
      <SignIn open={openSignIn} setOpen={setOpenSignIn} />
    </div>
  );
};

export default Home;
